import * as React from 'react';

import { providers, TOTAL_DOM_FS_ALLOWED_SYMBOLS } from '../../utils/constants';
import { Provider } from '../../models/filespace/provider';
import { Providers, Region } from 'portal-shared-lib';

import Wizard, { WizardProperties, WizardState } from './Wizard';
import DescriptionWrapper from '../common/DescriptionWrapper';

import { PlanSharedStepState, PlanStepComponent, ProviderPlanType } from './components/PlanStep';
import ProviderStepComponent, { ProviderSharedStepState } from './components/ProviderStep';
import RegionStepComponent, { RegionSharedStepState } from './components/RegionStep';
import EndpointStepComponent, { EndpointSharedStepState } from './components/EndpointStep';
import AdvancedSettingsStepComponent, { AdvancedSettingsSharedStepState } from './components/AdvancedSettingsStep';
import { CredentialsSharedStepState } from './components/CredentialsStep';
import ReviewStepComponent, { ReviewSharedStepState } from './components/ReviewStep';
import { Filespace, FilespaceS3Region, FilespaceStorage } from 'fs-api-client';
import { createFilespace } from '../../services/filespace';
import { DomainInjectorProperties, injectDomain } from '../hocs/DomainInjector';
import { injectProfile, ProfileInjectorProperties } from '../hocs/ProfileInjector';
import { PageRoutes } from '../root/Routes';
import { errorHandler } from '../../services/errorHandler';
import { getStorageInterfaceTranslations, pushEventToAnalytics, StorageInterfaceTranslations } from '../../utils/utilFunctions';
import { createFilespaceApi } from '../../utils/createApi';
import { BillingPeriodInjectorProperties, injectBillingPeriod } from '../hocs/BillingPeriodInjector';
import { EventHandler } from '../hocs/EventHandler';
import { injectTransitionTypeHandlers } from '../hocs/transitionType/TransitionTypeInjector';
import { FilespaceNameWizardStep, FilespaceNameWizardStepState } from './components/FilespaceNameWizardStep';

enum StepKey {
    FileSpaceStep = 'Filespace name',
    PlanStep = 'Plan',
    ProviderStep = 'Provider',
    RegionStep = 'Cloud storage region',
    EndpointStep = 'Cloud storage endpoint',
    AdvancedSettingsStep = 'Advanced settings',
    CredentialsStep = 'Credentials',
    ReviewStep = 'Review'
}

enum FilespaceVersion  {
    V3 = 3,
    V4 = 4,
    V6 = 6
}

enum EndpointType {
    Url = 'Url',
    Region = 'Region'
}

let LUCIDLINK_PROVIDER = Providers.Wasabi;

interface EndpointCache {
    endpointRegion?: string;
    endpointUrl?: string;
    providerRegion?: string;
}

interface Reviewable {
    isReview: boolean;
}

interface FilespaceCreationWizardProperties extends
    WizardProperties,
    BillingPeriodInjectorProperties,
    DomainInjectorProperties,
    ProfileInjectorProperties { }

type ProviderEndpoint = { url: string, region: string };
type ProviderRegion = Region;
type ProviderRegionCache = { [key in Providers]?: ProviderEndpoint | ProviderRegion };

// This combines all the step shared states into a one wizard state.
interface FileSpaceCreationWizardState extends
    WizardState<StepKey>,
    PlanSharedStepState,
    FilespaceNameWizardStepState,
    ProviderSharedStepState,
    RegionSharedStepState,
    EndpointSharedStepState,
    AdvancedSettingsSharedStepState,
    CredentialsSharedStepState,
    ReviewSharedStepState,
    ProviderRegionCache,
    Reviewable {

    isCreatingFilespace: boolean;
    fileSpaceCreationError: string;
    isFilespaceNameAboveProviderLimit: boolean;
    disabledRegions: Array<FilespaceS3Region>;
    isPlanStepEnabled: boolean;
    isLucidLink: boolean | null;
}

class FilespaceCreationWizard extends Wizard<FilespaceCreationWizardProperties, FileSpaceCreationWizardState, StepKey> {
    constructor(props: FilespaceCreationWizardProperties) {
        super(props);

        this.onFilespaceStepCompleted = this.onFilespaceStepCompleted.bind(this);
        this.onPlanStepCompleted = this.onPlanStepCompleted.bind(this);
        this.onProviderStepCompleted = this.onProviderStepCompleted.bind(this);
        this.onRegionStepCompleted = this.onRegionStepCompleted.bind(this);
        this.onEndpointStepCompleted = this.onEndpointStepCompleted.bind(this);
        this.onAdvancedSettingsStepCompleted = this.onAdvancedSettingsStepCompleted.bind(this);
        this.onReviewStepCompleted = this.onReviewStepCompleted.bind(this);

        this.state = {
            filespaceName: '',
            providerPlanType: ProviderPlanType.LucidLinkAdvancedAWS,
            provider: null,
            providerRegion: null,
            // isV4Selected: this.props.transitionType !== TransitionType.V3ToV4,
            endpointUrl: '',
            endpointRegion: '',
            ownBucketNameProvided: false,
            isCreatingFilespace: false,
            isFilespaceNameAboveProviderLimit: false,
            bucketName: '',
            stepHistory: [],
            fileSpaceCreationError: '',
            isReview: false,
            blockSize: null,
            sliderIdx: null,
            blockSizeText: null,
            disabledRegions: [],
            shouldIncludeBlockSize: false,
            isPlanStepEnabled: false,
            isLucidLink: null,
        };
    }

    componentDidMount() {
        createFilespaceApi().then(async (api) => {
            const disabledRegions = await api.getDisabledRegions();
            this.setState({ disabledRegions: disabledRegions.body });
        });
        EventHandler.onFilespaceWizardEnter();
        this.setState({
            isPlanStepEnabled: true,
            providerPlanType: null
        });
    }

    componentDidUpdate(prevProps: Readonly<FilespaceCreationWizardProperties>) {
        if (this.props.transitionType === prevProps.transitionType) { return; }

        // const isV4Selected = this.props.transitionType !== TransitionType.V3ToV4;
        // this.setState({ isV4Selected });
    }

    protected goToStep(stepKey: StepKey, additionalState: object = {}) {
        this.resetTemporaryState(additionalState as any);
        super.goToStep(stepKey, additionalState);
    }

    protected goBack(additionalState: object = {}) {
        this.resetTemporaryState(additionalState as any);
        super.goBack(additionalState);
    }

    protected stepRendered(): void {
        window.scrollTo(0, 0);
    }

    protected getFirstStepKey(): StepKey {
        return StepKey.PlanStep;
    }

    protected urlStepNameToStepKey(urlStepName: string): StepKey | null {
        let stepKeys = Object.keys(StepKey);
        for (let i = 0; i < stepKeys.length; i++) {
            let transformedStepName = this.stepKeyToUrlStepName(StepKey[stepKeys[i]]);
            if (transformedStepName === urlStepName) {
                return StepKey[stepKeys[i]];
            }
        }
        return null;
    }

    protected stepKeyToUrlStepName(stepKey: StepKey): string {
        return stepKey.toString().split(' ').join('').toLowerCase();
    }

    protected createStepElement(stepKey: StepKey): JSX.Element | null {
        let previousStepKey = this.getPreviousStepKey();
        let previousPageTitle: string = previousStepKey ? previousStepKey.toString() : '';

        const provider = this.state.provider;

        let translations: StorageInterfaceTranslations | null  = null;
        if (provider) {
            translations = getStorageInterfaceTranslations(provider.providerData.objectStore);
        } else {
            if ([
                StepKey.AdvancedSettingsStep, StepKey.EndpointStep, StepKey.RegionStep, StepKey.ReviewStep
            ].includes(stepKey)) {
                stepKey = StepKey.PlanStep;
            }
        }

        return this.buildStep(stepKey, previousPageTitle, provider, translations);
    }

    private buildStep(
        stepKey: StepKey,
        previousPageTitle: string,
        provider: Provider | null,
        translations: null | StorageInterfaceTranslations
    ) {
        switch (stepKey) {
            case StepKey.PlanStep:
                return this.getPlanStep();

            case StepKey.FileSpaceStep:
                return this.getFilespaceStep(previousPageTitle);

            case StepKey.ProviderStep:
                return this.getProviderStep(previousPageTitle);

            case StepKey.RegionStep: { // Create scope.
                return this.getRegionStep(provider, previousPageTitle, translations);
            }

            case StepKey.EndpointStep:
                return this.getEndpointStep(previousPageTitle);

            case StepKey.AdvancedSettingsStep:
                return this.getAdvancedSettingsStep(previousPageTitle, translations);

            case StepKey.ReviewStep:
                return this.getReviewStep(provider, previousPageTitle, translations);

            default:
                throw new Error('Step missing');
        }
    }

    private getReviewStep(provider: Provider | null, previousPageTitle: string, translations: StorageInterfaceTranslations | null) {
        if (!provider) {
            return null;
        }

        let onEndpointEntityEdit = () => {
            this.goToStep(StepKey.EndpointStep, {isReview: true});
        };

        return (
            <DescriptionWrapper
                title="Review and confirm"
                description="If everything looks good, click the create button below to proceed."
                previousPageTitle={previousPageTitle}
                onPreviousPageLinkClick={this.goBack}
            >
                <ReviewStepComponent
                    transitionType={this.props.transitionType}
                    storageContainerName={translations!.storageUnitName}
                    regionRequired={!!this.state.provider!.providerData!.regionRequired}
                    errorMessage={this.state.fileSpaceCreationError}
                    buttonIsLoading={this.state.isCreatingFilespace}
                    isPlanEditEnabled={this.state.isPlanStepEnabled}
                    onStepCompleted={this.onReviewStepCompleted}
                    savedState={this.state}
                    domainName={this.props.domain.name}
                    reviewData={{
                        plan: {
                            currentValue: this.state.providerPlanType,
                            onEdit: () => {
                                this.goToStep(StepKey.PlanStep, {isReview: true});
                            }
                        },
                        name: {
                            currentValue: this.state.filespaceName,
                            onEdit: () => {
                                this.goToStep(StepKey.FileSpaceStep, {isReview: true});
                            }
                        },
                        provider: {
                            currentValue: this.state.provider!.providerData.primaryName,
                            onEdit: () => {
                                this.goToStep(StepKey.ProviderStep, {isReview: true});
                            }
                        },
                        providerHasRegions: this.state.provider!.hasRegions(),
                        region: {
                            currentValue: this.state.providerRegion ? this.state.providerRegion!.name : null,
                            onEdit: () => {
                                let nextStepKey: StepKey = this.getStepBasedOnProvider(this.state.provider!);
                                this.goToStep(nextStepKey, {isReview: true});
                            }
                        },
                        // isV4Selected: this.state.isV4Selected,
                        endpointUrl: {
                            currentValue: this.state.endpointUrl ? this.state.endpointUrl : null,
                            onEdit: onEndpointEntityEdit
                        },
                        endpointRegion: {
                            currentValue: this.state.endpointRegion ? this.state.endpointRegion : null,
                            onEdit: onEndpointEntityEdit
                        },
                        bucketName: this.state.ownBucketNameProvided ?
                            {
                                currentValue: this.state.bucketName,
                                onEdit: () => {
                                    this.goToStep(StepKey.AdvancedSettingsStep, {isReview: true});
                                }
                            } :
                            null,
                        blockSizeText: this.state.blockSizeText !== null ?
                            {
                                currentValue: this.state.blockSizeText,
                                onEdit: () => {
                                    this.goToStep(StepKey.AdvancedSettingsStep, {isReview: true});
                                }
                            } :
                            null
                    }}
                />
            </DescriptionWrapper>
        );
    }

    private getAdvancedSettingsStep(previousPageTitle: string, translations: StorageInterfaceTranslations | null) {
        const openChatFn = () => {
            const isChatOpened = document.getElementsByClassName('chatlio-open')[0];
            if (isChatOpened) {
                return;
            }
            const element = document.getElementsByClassName('chatlio-title-bar-content')[0] as HTMLElement;
            if (element) {
                element.click();
            }
        };
        const description = (
            <p>In this section you can configure some advanced&nbsp;
                settings specific to your Filespace. <br/><br/>Note that these settings&nbsp;
                cannot be changed once the filespace is created. If you need further&nbsp;
                details or help with any of the settings do not hesitate to&nbsp;
                <a href="javascript:void(0)" onClick={openChatFn}>contact us</a>.</p>);
        return (
            <DescriptionWrapper
                title="Advanced Settings"
                description=""
                previousPageTitle={previousPageTitle}
                descriptionAsReactFragment={description}
                onPreviousPageLinkClick={this.goBack}
            >
                <AdvancedSettingsStepComponent
                    transitionType={this.props.transitionType}
                    storageContainerName={translations!.storageUnitName}
                    isLucidLink={this.state.isLucidLink!}
                    onStepCompleted={this.onAdvancedSettingsStepCompleted}
                    savedState={this.state}
                    defaultBlockSize={this.state.provider!.providerData.defaultBlockSize}
                    maxBucketNameLength={this.state.provider!.providerData.maxBucketNameLength}
                />
            </DescriptionWrapper>
        );
    }

    private getEndpointStep(previousPageTitle: string) {
        if (!this.state.provider) {
            return null;
        }
        const endpointExample: string | undefined = this.state.provider!.providerData.endpointExample;
        return (
            <DescriptionWrapper
                title="Endpoint URL"
                description="Please provide the endpoint URL and region name from your storage provider. Note that some providers use specific endpoints for each region, while others may use a generic endpoint for all regions."
                previousPageTitle={previousPageTitle}
                onPreviousPageLinkClick={this.goBack}
            >
                <EndpointStepComponent
                    exampleUrl={endpointExample ? endpointExample : 'http://provider:port'}
                    regionRequired={this.state.provider!.providerData!.regionRequired}
                    onStepCompleted={this.onEndpointStepCompleted}
                    savedState={this.state}
                />
            </DescriptionWrapper>
        );
    }

    private getRegionStep(provider: Provider | null, previousPageTitle: string, translations: StorageInterfaceTranslations | null) {
        let regionsList = provider ? provider.providerData.regionsList : [];
        if (!provider || !provider.providerData) {
            return null;
        }

        let warningDescription = undefined;
        if (this.state.providerPlanType === ProviderPlanType.LucidLinkCustom) {
            warningDescription = 'Keep in mind that your storage provider pricing may vary by region.';
        }
        const providerString = this.state.provider!.providerData.key.toString();

        const availableRegions = regionsList.filter(x => this.state.isLucidLink ? x.isLucidLinkProvided : true);
        const disabledRegions = this.state.disabledRegions
            .filter((x: FilespaceS3Region) => {
                return x.provider === providerString &&
                    x.isLucidLinkOffering ? this.state.isLucidLink : true;
            })
            .map((x: FilespaceS3Region) => { return x.name; });

        return (
            <DescriptionWrapper
                title="Choose cloud storage region"
                description="Selecting a region closest to your client devices offers the best performance. "
                previousPageTitle={previousPageTitle}
                onPreviousPageLinkClick={this.goBack}
                warningDescription={warningDescription}
            >
                <RegionStepComponent
                    provider={providerString}
                    disabledRegions={disabledRegions}
                    regions={availableRegions}
                    storageContainerName={translations!.storageUnitName}
                    isLucidLink={this.state.isLucidLink!}
                    onStepCompleted={this.onRegionStepCompleted}
                    savedState={this.state}
                />
            </DescriptionWrapper>
        );
    }

    private getProviderStep(previousPageTitle: string) {
        return (
            <DescriptionWrapper
                title="Choose your provider"
                description="LucidLink supports multiple storage providers. Please make sure you have:"
                previousPageTitle={previousPageTitle}
                onPreviousPageLinkClick={this.goBack}
                notes={[
                    <React.Fragment key="0"><strong>an account</strong> already configured with your chosen
                        provider</React.Fragment>,
                    <React.Fragment key="1">security <strong>credentials</strong> for access to the
                        provider</React.Fragment>
                ]}
            >
                <ProviderStepComponent
                    onStepCompleted={this.onProviderStepCompleted}
                    savedState={this.state}
                />
            </DescriptionWrapper>
        );
    }

    private getFilespaceStep(previousPageTitle: string) {
        const maxBucketNameLength = this.state.provider ?
            this.state.provider!.providerData.maxBucketNameLength : TOTAL_DOM_FS_ALLOWED_SYMBOLS;
        return (
            <DescriptionWrapper
                title="Choose a Filespace name"
                description="Choose a name that identifies this namespace within your domain."
                previousPageTitle={previousPageTitle}
                onPreviousPageLinkClick={this.goBack}
            >
                <FilespaceNameWizardStep
                    domainName={this.props.domain.name}
                    filespaces={this.props.filespaces.map(x => x.name)}
                    onStepCompleted={this.onFilespaceStepCompleted}
                    savedState={this.state}
                    maxNameLength={maxBucketNameLength}
                />
            </DescriptionWrapper>
        );
    }

    private getPlanStep() {
        const canCreateFilespace = !this.props.domain.migrationStartedAt || process.env.REACT_APP_ENV_NAME === 'be';
        if (!canCreateFilespace) {
            return (
                <DescriptionWrapper
                    title="Complete your upgrade"
                    description="You have started upgrading your domain and cannot create new filespaces anymore. Complete the upgrade and enjoy the benefits of the new LucidLink."
                    previousPageTitle="Dashboard"
                    onPreviousPageLinkClick={() => {
                        this.props.history.push(PageRoutes.Dashboard);
                    }}
                />
            );
        }
        
        const isLucidLinkEmail = this.props.email && (this.props.email.indexOf('@lucidlink.com') > 0);
        return (
            <DescriptionWrapper
                title="Choose Filespace type"
                description="LucidLink provides you with several easy storage options:"
                descriptionAsReactFragment={
                    <ul className="bullets">
                        <li className="orange">The LucidLink Classic Advanced and LucidLink Classic Enterprise Filespace
                            are ideal for performance sensitive workloads like video editing from the cloud.
                        </li>
                        <li className="blue">The LucidLink Classic Custom Filespace is ideal for full storage control.</li>
                        <li className="grey">The LucidLink Classic Basic Filespace is ideal
                            for cost sensitive applications like backup and archive.
                        </li>
                    </ul>
                }
                previousPageTitle="Dashboard"
                onPreviousPageLinkClick={() => {
                    this.props.history.push(PageRoutes.Dashboard);
                }}
            >
                <PlanStepComponent
                    isMetadataStreamingCheckboxDisabled={!isLucidLinkEmail}
                    transitionType={this.props.transitionType}
                    onStepCompleted={this.onPlanStepCompleted}
                    savedState={this.state}
                />
            </DescriptionWrapper>
        );
    }

    private resetTemporaryState(state: FileSpaceCreationWizardState) {
        state.fileSpaceCreationError = '';
        state.isCreatingFilespace = false;
    }

    private onPlanStepCompleted(stepState: PlanSharedStepState): void {
        switch (stepState.providerPlanType) {
            default:
            case ProviderPlanType.LucidLinkBasic:
                LUCIDLINK_PROVIDER = Providers.Wasabi;
                break;
            case ProviderPlanType.LucidLinkAdvancedAWS:
                LUCIDLINK_PROVIDER = Providers.AWS;
                break;
            case ProviderPlanType.LucidLinkAdvancedIBM:
                LUCIDLINK_PROVIDER = Providers.IBM;
                break;

        }
        const lucidLinkProvider = providers.filter(provider => provider.providerData.key === LUCIDLINK_PROVIDER)[0];

        const lucidLinkPlanTypes = [ ProviderPlanType.LucidLinkBasic, ProviderPlanType.LucidLinkAdvancedAWS, ProviderPlanType.LucidLinkAdvancedIBM ];
        const isLucidLink = lucidLinkPlanTypes.includes(stepState.providerPlanType!);

        const { bucketName, ownBucketNameProvided, provider: noShadowedProvider, providerRegion } = this.state;
        const additionalState: AdvancedSettingsSharedStepState &
            ProviderSharedStepState &
            RegionSharedStepState &
            { isLucidLink: boolean } = {

            bucketName,
            ownBucketNameProvided,
            provider: noShadowedProvider,
            providerRegion,
            isFilespaceNameAboveProviderLimit: this.state.isFilespaceNameAboveProviderLimit,
            // isV4Selected: stepState.isV4Selected,
            blockSize: this.state.blockSize,
            sliderIdx: this.state.sliderIdx,
            blockSizeText: this.state.blockSizeText,
            shouldIncludeBlockSize: this.state.shouldIncludeBlockSize,
            isLucidLink,
        };

        if (isLucidLink) {
            additionalState.ownBucketNameProvided = false;
            additionalState.provider = lucidLinkProvider;
            additionalState.providerRegion = this.getCachedLucidlinkStorageProviderRegion(lucidLinkProvider);
        }

        let nextStepKey: StepKey | null = null;
        if (this.state.isReview) {
            // TODO: Make it remember other provider regions.
            if (this.state.provider && this.state[this.state.provider.toString() + this.state.providerPlanType]) {
                nextStepKey = StepKey.ReviewStep;
            } else {
                nextStepKey = StepKey.RegionStep;
            }
        } else {
            nextStepKey = StepKey.FileSpaceStep;
        }
        this.mergeStepStateAndContinue(stepState, nextStepKey, additionalState);
    }

    private getCachedLucidlinkStorageProviderRegion(lucidlinkProvider: Provider): Region | null {
        const cachedLucidlinkProviderRegionName: Region | null =
            this.state[LUCIDLINK_PROVIDER + this.state.providerPlanType] ?
                this.state[LUCIDLINK_PROVIDER + this.state.providerPlanType] as Region :
                null;
        const cachedLucidlinkProviderRegion: Region[] =
            lucidlinkProvider.providerData.regionsList.filter(
                (region: any) => {
                    return cachedLucidlinkProviderRegionName ?
                        (region.name === cachedLucidlinkProviderRegionName.name) :
                        false;
                }
            );
        return cachedLucidlinkProviderRegion[0];
    }

    private onFilespaceStepCompleted(state: FilespaceNameWizardStepState): void {
        let nextStepKey: StepKey | null = null;

        if (this.state.isReview) {
            nextStepKey = StepKey.ReviewStep;
        } else if (this.state.providerPlanType === ProviderPlanType.LucidLinkCustom) {
            nextStepKey = StepKey.ProviderStep;
        } else {
            nextStepKey = StepKey.RegionStep;
        }

        // Set default provider.
        const lucidLinkProvider = providers.filter(provider => provider.providerData.key === LUCIDLINK_PROVIDER)[0];
        let lucidlinkProvider = this.state.provider ? this.state.provider : lucidLinkProvider;

        if (this.state.isFilespaceNameAboveProviderLimit) {
            nextStepKey = StepKey.RegionStep;
        }
        let finalState = {
            isFilespaceNameAboveProviderLimit: false,
            ...state,
            provider: lucidlinkProvider // Overwrite the old provider (which is probably null)
        };
        this.mergeStepStateAndContinue(finalState, nextStepKey);
    }

    private onProviderStepCompleted(state: ProviderSharedStepState): void {
        if (!state.provider) {
            return;
        }
        let nextStepKey: StepKey | null = this.getStepBasedOnProvider(state.provider);
        if (!this.isFilespaceNameInLimit(state.provider)) {
            nextStepKey = StepKey.FileSpaceStep;
            state.isFilespaceNameAboveProviderLimit = true;
        }
        this.mergeStepStateAndContinue(
            state,
            nextStepKey,
            this.lazyLoadEndpointCache(state.provider) // Load endpoint cache to display it in the next step.
        );
    }

    private lazyLoadEndpointCache(provider: Provider): EndpointCache {
        const endpointType:
            EndpointType = provider.hasRegions() ?
                EndpointType.Region :
                EndpointType.Url;
        let cachedEndpoint: EndpointCache | null = this.getEndpointCache(provider, endpointType);
        if (!cachedEndpoint) {
            cachedEndpoint = {
                endpointRegion: '',
                endpointUrl: '',
                providerRegion: ''
            };
        }

        return cachedEndpoint;
    }

    private onRegionStepCompleted(state: RegionSharedStepState): void {
        let nextStepKey: StepKey;

        if (this.shouldGoToBucketStep(state)) {
            nextStepKey = StepKey.AdvancedSettingsStep;
        } else {
            nextStepKey = StepKey.ReviewStep;
        }

        this.mergeStepStateAndContinue(
            state,
            nextStepKey,
            this.getEndpointToBeCached(state, EndpointType.Region) // Cache chosen endpoint.
        );
    }

    private onEndpointStepCompleted(state: EndpointSharedStepState): void {
        let nextStepKey: StepKey;

        if (this.shouldGoToBucketStep(state)) {
            nextStepKey = StepKey.AdvancedSettingsStep;
        } else {
            nextStepKey = StepKey.ReviewStep;
        }

        this.mergeStepStateAndContinue(
            state,
            nextStepKey,
            this.getEndpointToBeCached(state, EndpointType.Url) // Cache chosen endpoint.
        );
    }

    private shouldGoToBucketStep(state: RegionSharedStepState | EndpointSharedStepState): boolean {
        if (!state.ownBucketNameProvided) { return false; }

        return (this.state.isReview && !this.state.bucketName) || !this.state.isReview;
    }

    private getEndpointToBeCached(state: RegionSharedStepState | EndpointSharedStepState, endpointType: EndpointType): object {
        const cache = {};

        if (endpointType === EndpointType.Region) {
            cache[this.state.provider!.providerData.key] = (state as RegionSharedStepState).providerRegion;
        } else if (endpointType === EndpointType.Url) {
            const castedState = state as EndpointSharedStepState;
            cache[this.state.provider!.providerData.key] = {
                url: castedState.endpointUrl,
                region: castedState.endpointRegion
            };
        }

        return { ...cache };
    }

    private onAdvancedSettingsStepCompleted(state: AdvancedSettingsSharedStepState): void {
        this.mergeStepStateAndContinue(state, StepKey.ReviewStep);
    }

    private resolveSubscriptionType() {
        switch (this.state.providerPlanType) {
            case ProviderPlanType.LucidLinkBasic:
                return Filespace.SubscriptionTypeEnum.Basic;
            case ProviderPlanType.LucidLinkAdvancedAWS:
                return Filespace.SubscriptionTypeEnum.Advanced;
            case ProviderPlanType.LucidLinkAdvancedIBM:
                return Filespace.SubscriptionTypeEnum.Advanced;
            case ProviderPlanType.LucidLinkCustom:
                return Filespace.SubscriptionTypeEnum.Custom;
            default:
                return Filespace.SubscriptionTypeEnum.None;
        }
    }

    private onReviewStepCompleted(): void {
        if (!this.state.provider) {
            return;
        }

        let regionData: { region: string; endpoint: string } = {
            region: '',
            endpoint: ''
        };
        if (this.state.provider.hasRegions() && this.state.providerRegion) {
            regionData.region = this.state.providerRegion.region;

            // Amazon SDK specifics (ask Max).
            if (this.state.provider.providerData.key !== Providers.AWS) {
                regionData.endpoint = this.state.providerRegion.endpoint;
            }
        } else {
            regionData.region = this.state.endpointRegion;
            regionData.endpoint = this.state.endpointUrl;
        }

        let filespace = new Filespace();
        filespace.domain = this.props.domain.id!;
        filespace.name = this.state.filespaceName;
        filespace.type = Filespace.TypeEnum.Unlimited;
        filespace.subscriptionType = this.resolveSubscriptionType();
        filespace.storage = {
            provider: FilespaceStorage.ProviderEnum[this.state.provider.providerData.key],
            region: regionData.region,
            endpoint: regionData.endpoint,
            owner: this.state.isLucidLink ?
                FilespaceStorage.OwnerEnum.Lucidlink :
                FilespaceStorage.OwnerEnum.Customer,
            bucket: this.state.bucketName
        };
        filespace.version = FilespaceVersion.V6;
        if (this.state.shouldIncludeBlockSize && this.state.blockSize) {
            filespace.storage.blockSize = this.state.blockSize;
        }
        this.setState(
            { isCreatingFilespace: true },
            () => {
                createFilespace(filespace)
                    .then(() => {
                        pushEventToAnalytics('filespace_created');
                        this.props.history.push({
                            pathname: PageRoutes.Dashboard,
                            search: '?filespaceCreated'
                        });
                    })
                    .catch((error: any) => {
                        const errorMessage = error.message || error.response.body.message;
                        errorHandler(error);
                        this.setState({
                            isCreatingFilespace: false,
                            fileSpaceCreationError: errorMessage
                        });
                    });
            }
        );
    }

    private getStepBasedOnProvider(provider: Provider): StepKey {
        if (provider && provider.hasRegions()) {
            return StepKey.RegionStep;
        } else {
            return StepKey.EndpointStep;
        }
    }

    private getCachedProviderRegion(provider: Providers): ProviderRegion | undefined {
        return this.state[provider.toString() + this.state.providerPlanType] as ProviderRegion;
    }

    private getCachedProviderEndpoint(provider: Providers): ProviderEndpoint | undefined {
        return this.state[provider.toString() + this.state.providerPlanType] as ProviderEndpoint;
    }

    private getEndpointCache(provider: Provider, endpointType: EndpointType): EndpointCache | null {
        let result: object | null = null;
        if (!provider) {
            return null;
        }

        switch (endpointType) {
            case EndpointType.Region:
                const cachedProviderRegion = this.getCachedProviderRegion(provider.providerData.key);
                if (cachedProviderRegion) {
                    result = {
                        providerRegion: this.getCachedProviderRegion(provider.providerData.key)
                    };
                }
                break;
            case EndpointType.Url:
                const cachedProviderEndpoint = this.getCachedProviderEndpoint(provider.providerData.key);
                if (cachedProviderEndpoint) {
                    result = {
                        endpointUrl: cachedProviderEndpoint.url,
                        endpointRegion: cachedProviderEndpoint.region
                    };
                }
                break;
            default:
                break;
        }

        return result;
    }

    private isFilespaceNameInLimit(provider: Provider): boolean {
        return provider.providerData.maxBucketNameLength >= this.state.filespaceName.length;
    }
}

const InjectedCreationWizard = injectBillingPeriod(
    injectProfile(
        injectDomain(
            injectTransitionTypeHandlers(
                FilespaceCreationWizard
            ) as any
        ) as any
    ) as any
);
export { InjectedCreationWizard as FilespaceCreationWizard };



// WEBPACK FOOTER //
// ./src/webportal/components/wizard/FilespaceCreationWizard.tsx